/**
 * Login Component
 * 
 * Authentication page for the wedding website.
 * Handles both guest token authentication and master password access.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';

// Import centralized auth utilities
import { setAuthSession } from '../utils/auth';

function Login() {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [guestName, setGuestName] = useState('');
  const [expectedPassword, setExpectedPassword] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('/');
  const [isExpired, setIsExpired] = useState(false);
  const [hasValidToken, setHasValidToken] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const nameParam = urlParams.get('name') || '';
    const passwordParam = urlParams.get('pwd') || '';
    const tokenParam = urlParams.get('token') || '';
    const redirectParam = urlParams.get('redirect') || '';
    const expiresParam = urlParams.get('expires') || '';
    const errorParam = urlParams.get('error') || '';
    
    // Handle error parameters from auth validation
    if (errorParam) {
      switch (errorParam) {
        case 'missing_redirect':
          setError('❌ Missing redirect information. Please use your personalized invitation link.');
          break;
        case 'invalid_token_page':
          setError('🚫 Invalid token for this page. This token was not meant for this page.');
          break;
        case 'invalid_redirect':
          setError('❌ Invalid link format. Please use your personalized invitation link.');
          break;
        case 'access_denied':
          setError('🚫 Access denied. You need Super Admin privileges to access this page.');
          break;
        default:
          setError('❌ Access denied. Please use your personalized invitation link.');
      }
    }
    
    // Check if we have a token (indicating this is a valid link)
    if (tokenParam && nameParam && passwordParam) {
      setHasValidToken(true);
      // Decode base64 parameters
      try {
        const decodedName = nameParam ? atob(nameParam) : '';
        console.log('Decoded name:', decodedName);
        setGuestName(decodedName);
        setExpectedPassword(passwordParam ? atob(passwordParam) : '');
        // Decode redirect parameter if provided, otherwise default to '/'
        setRedirectUrl(redirectParam ? atob(redirectParam) : '/');
        
        // Check expiration if provided
        if (expiresParam) {
          const expirationDate = new Date(atob(expiresParam));
          const currentDate = new Date();
          if (currentDate > expirationDate) {
            setIsExpired(true);
            setError('🕐 This link has expired. Please request a new invitation link.');
          }
        }
      } catch (e) {
        setGuestName('');
        setExpectedPassword('');
        setRedirectUrl('/');
        setError('❌ Invalid link format.');
        setHasValidToken(false);
      }
    } else {
      // No valid token/parameters, show master login option
      setHasValidToken(false);
      setGuestName('');
      setExpectedPassword('');
      setRedirectUrl('/fullAgenda');
    }
  }, []);

  const verify = () => {
    // Check if link is expired first
    if (isExpired) {
      setError('🕐 This link has expired. Please request a new invitation link.');
      setPassword(''); // Clear password field on expiration
      return;
    }

    const inputPassword = password.trim();

    if (inputPassword === expectedPassword) {
      setError('');
      // Set authentication using centralized utility
      setAuthSession(guestName, 'guest');
      // Navigate to redirect URL WITH redirect parameter for auth validation
      navigate(redirectUrl + '?redirect=' + btoa(redirectUrl));
    } else {
      setError('❌ Password does not match. Access denied.');
      setPassword(''); // Clear password field on failure
    }
  };


  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      verify();
    }
  };

  console.log('Guest name in render:', guestName);
  
  return (
    <div className="login-container">
      {/* Envelope flap */}
      <div className="envelope-flap">
        <img src="/envelop_flap.png" alt="Envelope Flap" className="envelope-flap-image" />
      </div>
      
      {/* Hero text overlay similar to reception page */}
      <div className="login-hero-text">
        <img src={`${process.env.PUBLIC_URL}/logo2.png`} alt="Wedding Logo" className="login-hero-logo" />
        <h1>Radhika & Vivek</h1>
        <p>warmly invite you to their wedding ceremony</p>
      </div>
      
      <div className="login-card">
        <div className="login-header">
          {hasValidToken ? (
            guestName ? (
              <>
                <h1 className='wedding-couple-names'>Radhika & Vivek</h1>
                <h1 className="wedding-invitation-title">Wedding Invitation</h1>
                <div className="invitation-to">To,</div>
                <h2 className="guest-name-line">{guestName}</h2>
                <p>Please enter your password to authenticate.</p>
              </>
            ) : (
              <>
                <h2>🔐 Enter Your Details to Access</h2>
                <p>This page is personalized. Please confirm your identity to continue.</p>
              </>
            )
          ) : (
            <>
              <h2>Radhika & Vivek</h2>
              <p>Wedding invitation</p>
              <p> We have sent personalized links for invitations.</p>
            </>
          )}
        </div>

        <div className="login-form">
          {hasValidToken ? (
            <>
              <div className="input-group">
                <label htmlFor="password">Password:</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Your password"
                />
              </div>

              <button onClick={verify} className="submit-button" disabled={isExpired}>
                Open
              </button>
            </>
          ) : (
            <div className="guest-access-info">
              <p> Please use your link for access.</p>
              <p style={{fontSize: '0.9em', color: '#666', marginTop: '20px'}}>
                Contact us if you have issues accessing the site.
              </p>
            </div>
          )}

          {error && <p className="error">{error}</p>}
        </div>

        <div className="login-footer">
          <p>We look forward to seeing you at the wedding!</p>
        </div>
      </div>
      
      {/* Transparent footer with flower images */}
      <div className="login-page-footer">
        <div className="footer-flowers">
          <img src="/aboli.png" alt="Aboli Flower" className="footer-flower" />
          <img src="/marigold.png" alt="Marigold Flower" className="footer-flower" />
        </div>
      </div>
    </div>
  );
}

export default Login;